import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Droplets,
  Activity,
  MountainSnow,
  CloudRain,
  Radio,
  MapPin,
  Send,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Cloud,
} from "lucide-react";
import GridItem from "../../../../Components/UiComponents/GridItem/GridItem";
import { toDisplayRegionName } from "../../../../Components/UiComponents/utils";
import { postDataWithBodyEvent } from "../../../../Services/Services";
import * as EndPoints from "../../../../Services/EndPoints";

const GOOGLE_MAPS_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";

// Resolves a lat/lng into the most specific place name Google's Geocoding
// REST API can offer (village/locality/sublocality) — same API used
// elsewhere in the app (see OpenLayerSource.js's reverseGeocode). Google's
// administrative levels (district/state) are unreliable for the remote
// Ladakh terrain this app covers — often the only result is a bare
// postal-code area with no named locality at all (just digits, e.g.
// "194102") — so rather than lean on those, the caller stitches this
// locality onto the district/block the operator already picked
// (location.name), which is always known. Only used to enrich what's
// *displayed* in the Location & Sensor field; location.name itself (driving
// submission and the auto-generated description) is left untouched.
//
// `knownNames` (the district/block already picked) lets a bare postal-code
// result still resolve to a real name: Google lists the villages/towns each
// pincode covers under `postcode_localities`, so that list is searched for
// whichever entry matches what's already known before falling back to its
// first entry — anything beats showing the raw pincode digits.
const reverseGeocode = async (lat, lng, knownNames = []) => {
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
    );
    const data = await res.json();
    if (data.status === "OK" && data.results?.length > 0) {
      const get = (components, type) => components.find((c) => c.types.includes(type))?.long_name || "";
      const norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
      const knownNormalized = knownNames.filter(Boolean).map(norm);
      for (const result of data.results) {
        const formatted = (result.formatted_address || "").trim();
        const isPlusCode = /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,3}/.test(formatted);
        if (isPlusCode) continue;
        const components = result.address_components || [];
        // Deliberately never sourced from "postal_code" — that's the exact
        // component that turns into a bare pincode like "194401" once no
        // named place outranks it.
        let place =
          get(components, "premise") ||
          get(components, "sublocality_level_1") ||
          get(components, "sublocality") ||
          get(components, "neighborhood") ||
          get(components, "locality") ||
          get(components, "route");
        if (!place && Array.isArray(result.postcode_localities) && result.postcode_localities.length > 0) {
          place =
            result.postcode_localities.find((p) => {
              const np = norm(p);
              return knownNormalized.some((n) => np.startsWith(n.slice(0, 4)) || n.startsWith(np.slice(0, 4)));
            }) || result.postcode_localities[0];
        }
        if (place) return place;
        // Last resort: the result's own formatted_address, but with any
        // standalone pincode token (e.g. the ", 194401" Google tacks onto a
        // route-only result) stripped out first.
        const withoutPincode = formatted.replace(/,?\s*\b\d{4,7}\b\s*/g, "").replace(/^,\s*|,\s*$/g, "").trim();
        if (withoutPincode) return withoutPincode;
      }
    }
    return null;
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────────────────────────────────
// Hazard configuration — ported from alert_sender.py / alert_ui.py
// (thresholds §5, 4-level UI bands §6, location/sensor registry §7).
// ─────────────────────────────────────────────────────────────────────────
const HAZARDS = {
  FLOOD: {
    key: "FLOOD",
    label: "Flood",
    unit: "meters",
    icon: Droplets,
    color: "#2563EB",
    stable: [0.0, 0.5],
    warning: [0.5, 1.0],
    alert: [1.0, 10.0],
    critical: 1.0,
    levels: [
      { level: 1, label: "Level 1 – Minor", min: 1.0, max: 2.0 },
      { level: 2, label: "Level 2 – Moderate", min: 2.1, max: 3.0 },
      { level: 3, label: "Level 3 – Major", min: 3.1, max: 5.0 },
      { level: 4, label: "Level 4 – Critical", min: 5.1, max: 10.0 },
    ],
    defaultLocation: "LEH, DISKIT",
    locations: [
      { name: "LEH, DISKIT", lat: 34.5505, lng: 77.5622, sensorId: "FLOOD_SENSOR_01" },
      { name: "LEH, KARU", lat: 33.916061, lng: 77.735036, sensorId: "FLOOD_SENSOR_02" },
      { name: "KARGIL, DRASS", lat: 34.428998, lng: 75.750494, sensorId: "FLOOD_SENSOR_03" },
      { name: "KARGIL, ZANSKAR", lat: 33.475598, lng: 76.88819, sensorId: "FLOOD_SENSOR_04" },
    ],
  },
  CLOUDBURST: {
    key: "CLOUDBURST",
    label: "Cloud Burst",
    unit: "mm/hr",
    icon: CloudRain,
    color: "#0D9488",
    stable: [10.0, 20.0],
    warning: [20.0, 30.0],
    alert: [30.0, 300.0],
    critical: 30.0,
    levels: [
      { level: 1, label: "Level 1 – Minor", min: 30, max: 50 },
      { level: 2, label: "Level 2 – Moderate", min: 50.1, max: 80 },
      { level: 3, label: "Level 3 – Major", min: 80.1, max: 100 },
      { level: 4, label: "Level 4 – Critical", min: 100.1, max: 300 },
    ],
    defaultLocation: "LEH, DISKIT",
    locations: [
      { name: "LEH, DISKIT", lat: 34.5505, lng: 77.5622, sensorId: "CLOUDBURST_STN_01" },
      { name: "LEH, KARU", lat: 33.913104, lng: 77.712622, sensorId: "CLOUDBURST_STN_02" },
      { name: "KARGIL, DRASS", lat: 34.422674, lng: 75.744526, sensorId: "CLOUDBURST_STN_03" },
      { name: "KARGIL, ZANSKAR", lat: 33.54951, lng: 76.984151, sensorId: "CLOUDBURST_STN_04" },
    ],
  },
  AVALANCHE: {
    key: "AVALANCHE",
    label: "Avalanche",
    unit: "cm",
    icon: MountainSnow,
    color: "#7C3AED",
    stable: [10.0, 30.0],
    warning: [30.0, 60.0],
    alert: [60.0, 100.0],
    critical: 60.0,
    levels: [
      { level: 1, label: "Level 1 – Minor", min: 60, max: 70 },
      { level: 2, label: "Level 2 – Moderate", min: 70.1, max: 80 },
      { level: 3, label: "Level 3 – Major", min: 80.1, max: 90 },
      { level: 4, label: "Level 4 – Critical", min: 90.1, max: 100 },
    ],
    defaultLocation: "LEH, DISKIT",
    locations: [
      { name: "LEH, DISKIT", lat: 34.5505, lng: 77.5622, sensorId: "AVALANCHE_STN_01" },
      { name: "LEH, KARU", lat: 33.986633, lng: 77.826271, sensorId: "AVALANCHE_STN_02" },
      { name: "KARGIL, DRASS", lat: 34.419616, lng: 75.757133, sensorId: "AVALANCHE_STN_03" },
      { name: "KARGIL, MUSHKO VALLEY", lat: 33.893055, lng: 76.383611, sensorId: "AVALANCHE_STN_04" },
    ],
  },
  EARTHQUAKE: {
    key: "EARTHQUAKE",
    label: "Earthquake",
    unit: "Richter",
    icon: Activity,
    color: "#D97706",
    stable: [0.0, 2.0],
    warning: [2.0, 4.0],
    alert: [4.0, 10.0],
    critical: 4.0,
    levels: [
      { level: 1, label: "Level 1 – Minor", min: 4.0, max: 4.9 },
      { level: 2, label: "Level 2 – Moderate", min: 5.0, max: 5.9 },
      { level: 3, label: "Level 3 – Major", min: 6.0, max: 6.9 },
      { level: 4, label: "Level 4 – Critical", min: 7.0, max: 10.0 },
    ],
    defaultLocation: "LEH, DISKIT",
    locations: [
      { name: "LEH, DISKIT", lat: 34.5505, lng: 77.5622, sensorId: "SEISMIC_STN_01" },
      { name: "LEH, KARU", lat: 33.900752, lng: 77.756874, sensorId: "SEISMIC_STN_02" },
      { name: "KARGIL, DRASS", lat: 34.419616, lng: 75.757133, sensorId: "SEISMIC_STN_03" },
      { name: "KARGIL, ZANSKAR", lat: 33.553499, lng: 76.981439, sensorId: "SEISMIC_STN_04" },
    ],
  },
};

// Where the alert's telemetry is coming from — drives the "Select Alert
// Source" cards. IoT Device and Manual/Field Observation were removed per
// explicit request (Sensor is the only source this form actually creates
// alerts from); Third-Party API stays as a card but hands off to the CAP
// Alerts page instead of setting this state (see its onClick below), so
// "SENSOR" is the only value alertSource is ever actually set to.
const ALERT_SOURCES = [
  { key: "SENSOR", label: "Sensor", description: "Generate an alert from sensor telemetry", icon: Radio },
  { key: "THIRD_PARTY_API", label: "Third-Party API", description: "Simulate alert from NDMA, IMD, DGRE, SDMA or other APIs", icon: Cloud },
];

// Sensor Type, Sensor Name/ID, and measurement Unit options — all scoped
// per hazard, since "2. Hazard Information" is now filled in before
// "3. Source Details". Sensor Name/ID further narrows by whichever Sensor
// Type is picked. Changing the hazard resets both back to their first
// option (see selectHazard) so a stale mismatched value never lingers.
const HAZARD_SENSOR_CONFIG = {
  EARTHQUAKE: {
    sensorTypes: ["Seismometer", "Strong Motion Accelerometer"],
    sensorNames: {
      "Seismometer": ["SM_001 – LEH", "SM_002 – DISKIT", "SM_003 – KARGIL", "SM_004 – ZANSKAR", "SM_005 – KHALSI", "SM_006 – DRASS"],
      "Strong Motion Accelerometer": ["AC_001 – LEH", "AC_002 – DISKIT", "AC_003 – KARGIL", "AC_004 – ZANSKAR", "AC_005 – KHALSI", "AC_006 – DRASS"],
    },
    units: ["M", "g", "cm/s²"],
  },
  FLOOD: {
    sensorTypes: ["River Water Level Sensor", "River Discharge / Flow Sensor"],
    sensorNames: {
      "River Water Level Sensor": ["WL_001 – LEH", "WL_002 – DISKIT", "WL_003 – KARGIL", "WL_004 – ZANSKAR", "WL_005 – KHALSI", "WL_006 – DRASS"],
      "River Discharge / Flow Sensor": ["DQ_001 – LEH", "DQ_002 – DISKIT", "DQ_003 – KARGIL", "DQ_004 – ZANSKAR", "DQ_005 – KHALSI", "DQ_006 – DRASS"],
    },
    units: ["m", "cm", "m³/s"],
  },
  AVALANCHE: {
    sensorTypes: ["Snow Depth Sensor", "Automatic Snow & Weather Station"],
    sensorNames: {
      "Snow Depth Sensor": ["SD_001 – LEH", "SD_002 – DISKIT", "SD_003 – KARGIL", "SD_004 – ZANSKAR", "SD_005 – KHALSI", "SD_006 – DRASS"],
      "Automatic Snow & Weather Station": ["ASW_001 – LEH", "ASW_002 – DISKIT", "ASW_003 – KARGIL", "ASW_004 – ZANSKAR", "ASW_005 – KHALSI", "ASW_006 – DRASS"],
    },
    units: ["Danger level (1–5)", "cm"],
  },
  CLOUDBURST: {
    sensorTypes: ["Rain Gauge (ARG)", "Automatic Weather Station"],
    sensorNames: {
      "Rain Gauge (ARG)": ["RG_001 – LEH", "RG_002 – DISKIT", "RG_003 – KARGIL", "RG_004 – ZANSKAR", "RG_005 – KHALSI", "RG_006 – DRASS"],
      "Automatic Weather Station": ["AWS_001 – LEH", "AWS_002 – DISKIT", "AWS_003 – KARGIL", "AWS_004 – ZANSKAR", "AWS_005 – KHALSI", "AWS_006 – DRASS"],
    },
    units: ["mm/hr", "mm"],
  },
};

// This form is used as a live-demo tool, so Source Details is pinned to one
// canned sensor per hazard (matching HAZARD_SENSOR_CONFIG's own lists above)
// rather than left open for the operator to pick — Sensor Type/Name-ID are
// shown disabled (see "3. Source Details" JSX) and always resolve to the
// same fixed location per hazard: Flood → Leh, Cloud Burst → Diskit,
// Avalanche → Kargil, Earthquake → Zanskar.
const HAZARD_FIXED_SOURCE = {
  FLOOD: { sensorType: "River Water Level Sensor", sensorNameId: "WL_001 – LEH" },
  CLOUDBURST: { sensorType: "Rain Gauge (ARG)", sensorNameId: "RG_002 – DISKIT" },
  AVALANCHE: { sensorType: "Snow Depth Sensor", sensorNameId: "SD_003 – KARGIL" },
  EARTHQUAKE: { sensorType: "Seismometer", sensorNameId: "SM_004 – ZANSKAR" },
};
// Measured Value is likewise pinned to a fixed demo reading …
const FIXED_MEASURED_VALUE = "5";

// Real coordinates for each of the 6 location codes Sensor Name/ID resolves
// to (see extractLocationFromSensorName) — LEH/KARGIL are both a district
// and a same-named block; DISKIT/KHALSI sit under LEH district, ZANSKAR/
// DRASS under KARGIL. Lets "4. Alert Details"'s Source Location auto-fill
// "5. Impact & Location" (Location & Sensor, State/District/Block,
// Coordinates) without another round trip through the map picker.
// "address" is a curated full address (not Google's reverse geocode, which
// tends to return bare plus-codes/pincodes out here — see reverseGeocode's
// own comment) — shown as-is in the Location & Sensor field whenever
// location.name matches one of these blocks (see the locationAddress
// effect below), whether reached via this auto-fill or a manual map pick.
// "regionId" is each block's GIS region id, as given for the payload's
// region reference — not derived from anything else in this file.
const BLOCK_COORDINATES = {
  LEH: { district: "LEH", block: "LEH", lat: 34.1526, lng: 77.5771, address: "Keylong-Leh Road, Leh, Leh", regionId: 5006 },
  DISKIT: { district: "LEH", block: "DISKIT", lat: 34.5505, lng: 77.5622, address: "Khardung La Road, Diskit, Leh", regionId: 5004 },
  KHALSI: { district: "LEH", block: "KHALSI", lat: 34.2736, lng: 76.8988, address: "Saspul, Khalsi, Leh", regionId: 5005 },
  KARGIL: { district: "KARGIL", block: "KARGIL", lat: 34.5492, lng: 76.1435, address: "Sankoo, Kargil, Kargil", regionId: 5008 },
  ZANSKAR: { district: "KARGIL", block: "ZANSKAR", lat: 33.475598, lng: 76.88819, address: "Padum, Zanskar, Kargil", regionId: 5002 },
  DRASS: { district: "KARGIL", block: "DRASS", lat: 34.428998, lng: 75.750494, address: "Srinagar - Leh Highway, Drass, Kargil", regionId: 5001 },
};
// "DISTRICT, BLOCK" (as location.name is formatted) → curated address,
// for the locationAddress effect below.
const BLOCK_ADDRESS_BY_NAME = Object.fromEntries(
  Object.values(BLOCK_COORDINATES).map((b) => [`${b.district}, ${b.block}`, b.address])
);
// Meters — fixed for now since this form has no Affected Radius input of
// its own; only used to draw the map preview's radius circle.
const DEFAULT_AFFECTED_RADIUS_M = 2000;

// Communication channel isn't operator-configurable — every alert created
// here goes out over the standard web alert channel.
const COMMUNICATION_TYPE = "WEB Alerts";

// STABLE/WARNING/ALERT colours unified with the Level 1–4 badge colours
// already used elsewhere in this app (Incident Category Matrix, etc.)
// rather than the classifier's separate WARNING/ALERT/EXTREME-ALERT
// palette from alert_ui.py — see blueprint §5/§6 "known colour
// inconsistency" callout for why these two didn't originally match.
const LEVEL_COLORS = ["#28a745", "#ffc107", "#fd7e14", "#dc3545"];

// API title-cases whatever severity string was sent; every string here is
// already properly cased except the literal "LOW" stable-path value.
function toApiSeverity(severity) {
  return severity === "LOW" ? "Low" : severity;
}

function hazardDisplayName(hazardKey) {
  if (hazardKey === "CLOUDBURST") return "Cloud Burst";
  return hazardKey
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

// Auto-generated Alert Title, e.g. "Flood Alert at Leh" — just the block
// name (see BLOCK_COORDINATES), not "Block, District".
function buildAlertTitle(hazardKey, regionDetails) {
  const name = hazardDisplayName(hazardKey);
  const { block } = regionDetails || {};
  if (!block) return `${name} Alert`;
  return `${name} Alert at ${toTitleCase(block)}`;
}

// Alert description templates, reproduced verbatim from blueprint §4 —
// including the AVALANCHE/CLOUDBURST multi-line bullet formats.
function buildAlertDescription(hazardKey, sendEventStatus, measuredValue, regionName, thresholdValue) {
  const name = hazardDisplayName(hazardKey);
  const prefix = sendEventStatus === "WARNING" ? "Warning" : sendEventStatus === "ALERT" ? "Alert" : "Normal";

  switch (hazardKey) {
    case "FLOOD":
      if (sendEventStatus === "WARNING")
        return `Flood Warning: Water level ${measuredValue} meters at ${regionName} - Approaching flood threshold (${thresholdValue} meters)`;
      if (sendEventStatus === "ALERT")
        return `Flood Alert: Water level ${measuredValue} meters at ${regionName} - Flood threshold (${thresholdValue} meters) exceeded`;
      return `Flood ${prefix}: Water level ${measuredValue}meters at ${regionName} - Within safe range (Threshold: ${thresholdValue} meters)`;

    case "EARTHQUAKE":
      if (sendEventStatus === "WARNING")
        return `Earthquake Warning: Magnitude ${measuredValue} Richter detected at ${regionName} - Approaching concern level (${thresholdValue} Richter)`;
      if (sendEventStatus === "ALERT")
        return `Earthquake Alert: Magnitude ${measuredValue} Richter detected at ${regionName} - Significant seismic activity (Threshold: ${thresholdValue} Richter)`;
      return `Earthquake ${prefix}: Magnitude ${measuredValue} Richter at ${regionName} - Minor activity (Threshold: ${thresholdValue} Richter)`;

    case "AVALANCHE":
      if (sendEventStatus === "WARNING")
        return `Avalanche Warning: Snow depth ${measuredValue} cm at ${regionName}\n- Unstable snow conditions developing\n- Rapid temperature rise 5°C observed\n- Unstable snowpack conditions approaching critical threshold (${thresholdValue} cm)\n- Exercise caution in high-altitude areas`;
      if (sendEventStatus === "ALERT")
        return `Avalanche Alert: Critical snow depth ${measuredValue} cm at ${regionName}\n- Heavy snow accumulation and instability detected\n- Avoid movement in slope zones immediately\n- Ground vibration observed - slope failure imminent\n- Threshold (${thresholdValue} cm) EXCEEDED - HIGH AVALANCHE RISK\n- Evacuate affected areas - DO NOT ACCESS SLOPES`;
      return `Avalanche ${prefix}: Snow depth ${measuredValue} cm at ${regionName} - Avalanche risk stable (Threshold: ${thresholdValue} cm)`;

    case "CLOUDBURST":
      if (sendEventStatus === "WARNING")
        return `Cloud Burst Warning: Intense rainfall ${measuredValue} mm/hr detected at ${regionName}\n- Approaching critical threshold (${thresholdValue} mm/hr)\n- Flash flood and landslide risk increasing`;
      if (sendEventStatus === "ALERT")
        return `Cloud Burst Alert: Extreme rainfall ${measuredValue} mm/hr recorded at ${regionName}\n- Critical threshold (${thresholdValue} mm/hr) EXCEEDED\n- High risk of flash floods and landslides`;
      return `Cloud Burst ${prefix}: Rainfall ${measuredValue} mm/hr at ${regionName} - Within safe range (Threshold: ${thresholdValue} mm/hr)`;

    default:
      return `${name} ${prefix} at ${regionName}`;
  }
}

// GIS endpoints return st_name/dist_name/blk_name in all caps (e.g.
// "LADAKH") — title-cased purely for the State/District/Block display row,
// same treatment toDisplayRegionName gives the Location & Sensor field.
function toTitleCase(s) {
  return (s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// Formats a Date as the "YYYY-MM-DDTHH:mm" value <input type="datetime-local">
// expects, in the browser's local time (never UTC — toISOString would shift
// the displayed clock time on any non-UTC machine).
function toLocalDateTimeInputValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Sensor/Device Name-ID and Observed By entries encode their location as
// the last " – "-separated segment (e.g. "SM_001 – LEH", "WL_002 – KHALSI")
// — picking one auto-fills Source Details' Location field with that
// segment. Location is disabled wherever this applies, so it can't drift
// from whatever was actually picked.
function extractLocationFromSensorName(sensorNameId) {
  const parts = (sensorNameId || "").split("–").map((p) => p.trim()).filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : "";
}

function makePinIcon(color) {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        width: 28px; height: 28px; border-radius: 50% 50% 50% 0;
        background: ${color};
        border: 3px solid #fff;
        transform: rotate(-45deg);
        box-shadow: 0 3px 8px rgba(0,0,0,0.35);
      "></div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

// MapContainer's `center` prop only applies on the *initial* mount — it
// won't re-pan the map when `location` changes afterwards. This keeps the
// view following the marker whenever a new point is confirmed.
const RecenterOnChange = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng], map.getZoom());
  }, [lat, lng, map]);
  return null;
};

// Base map tile options for the preview map's custom layers button — kept
// as a plain lookup (rather than react-leaflet's own <LayersControl>) so
// the toggle button can be styled and sized independently of Leaflet's
// fixed native control look.
const BASE_LAYERS = {
  "Road View": { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap contributors", subdomains: ["a", "b", "c"] },
  "Satellite View": { url: "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", attribution: "", subdomains: ["mt0", "mt1", "mt2", "mt3"] },
  "Terrain View": { url: "https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}", attribution: "", subdomains: ["mt0", "mt1", "mt2", "mt3"] },
  "Hybrid View": { url: "https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", attribution: "", subdomains: ["mt0", "mt1", "mt2", "mt3"] },
};

// Read-only preview of the confirmed alert location — kept visible in the
// main form (not just inside the map picker modal) so the pin stays in
// view after the modal closes. Same fullscreen-toggle pattern used on the
// Disaster Location map (AccidentViewer.js).
const LocationPreviewMap = ({ location, color, popupInfo }) => {
  const wrapperRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeBaseLayer, setActiveBaseLayer] = useState("Road View");
  const [layersMenuOpen, setLayersMenuOpen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      wrapperRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  if (!location) {
    return (
      <div
        className="ca-location-preview-map"
        style={{
          height: "260px",
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #EEF2F6",
          marginTop: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          background: "#F8FAFC",
          color: "#94A3B8",
          fontSize: "12.5px",
          fontWeight: 600,
          textAlign: "center",
          padding: "20px",
        }}
      >
        <MapPin size={20} color="#C7D2DA" />
        Select a location on the map to preview it here.
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="ca-location-preview-map"
      style={{ position: "relative", height: "260px", borderRadius: "10px", overflow: "hidden", border: "1px solid #EEF2F6", marginTop: "8px" }}
    >
      <MapContainer
        center={[location.lat, location.lng]}
        zoom={12}
        attributionControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          key={activeBaseLayer}
          attribution={BASE_LAYERS[activeBaseLayer].attribution}
          url={BASE_LAYERS[activeBaseLayer].url}
          maxZoom={20}
          subdomains={BASE_LAYERS[activeBaseLayer].subdomains}
        />
        <Circle
          center={[location.lat, location.lng]}
          radius={DEFAULT_AFFECTED_RADIUS_M}
          pathOptions={{ color: "#DC2626", weight: 1.5, fillColor: "#DC2626", fillOpacity: 0.15 }}
        />
        <Marker position={[location.lat, location.lng]} icon={makePinIcon(color)}>
          {popupInfo && (
            <Popup className="ca-alert-popup" minWidth={220} maxWidth={260}>
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 12px",
                    background: `linear-gradient(120deg, ${color} 0%, ${color}CC 100%)`,
                    color: "#fff",
                  }}
                >
                  <div
                    style={{
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.22)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {popupInfo.Icon && <popupInfo.Icon size={14} color="#fff" />}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "12.5px", fontWeight: 800, lineHeight: 1.25 }}>{popupInfo.hazardLabel} Alert</div>
                    <div style={{ fontSize: "10px", fontWeight: 700, opacity: 0.9 }}>{popupInfo.severity}</div>
                  </div>
                </div>
                <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: "7px", background: "#fff" }}>
                  {[
                    [MapPin, "Location", popupInfo.locationLabel],
                    [Radio, "Sensor", popupInfo.sensorNameId],
                    [Activity, "Measured Value", `${popupInfo.measuredValue} ${popupInfo.unit}`],
                  ].map(([RowIcon, label, value], i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                      <RowIcon size={12} color="#94A3B8" style={{ marginTop: "2px", flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: "9.5px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          {label}
                        </div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#0F2D42", wordBreak: "break-word" }}>{value}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ fontSize: "10.5px", color: "#94A3B8", borderTop: "1px dashed #E2E8F0", paddingTop: "6px", marginTop: "1px" }}>
                    {location.lat.toFixed(4)}°, {location.lng.toFixed(4)}°
                  </div>
                </div>
              </div>
            </Popup>
          )}
        </Marker>
        <RecenterOnChange lat={location.lat} lng={location.lng} />
      </MapContainer>

      {/* Strips Leaflet's default popup padding/border so the custom card
          markup above (header + rows) can own its own shape/shadow. */}
      <style>{`
        .ca-alert-popup .leaflet-popup-content-wrapper {
          padding: 0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 28px rgba(15,45,66,0.22);
        }
        .ca-alert-popup .leaflet-popup-content {
          margin: 0;
        }
        .ca-alert-popup .leaflet-popup-tip-container {
          filter: drop-shadow(0 2px 2px rgba(15,45,66,0.15));
        }
        .ca-alert-popup .leaflet-popup-close-button {
          color: #fff !important;
          z-index: 1;
        }
      `}</style>

      <button
        type="button"
        onClick={toggleFullscreen}
        title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          zIndex: 1000,
          width: 28,
          height: 28,
          borderRadius: 4,
          border: "none",
          background: "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 5px rgba(0,0,0,0.4)",
        }}
      >
        <svg viewBox="0 0 18 18" fill="none" width="16" height="16">
          {isFullscreen ? (
            <path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="#2E6DA4" strokeWidth="1.6" strokeLinecap="round" />
          ) : (
            <path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="#2E6DA4" strokeWidth="1.6" strokeLinecap="round" />
          )}
        </svg>
      </button>

      <div
        style={{ position: "absolute", top: "46px", right: "10px", zIndex: 1000 }}
        onMouseLeave={() => setLayersMenuOpen(false)}
      >
        <button
          type="button"
          onClick={() => setLayersMenuOpen((o) => !o)}
          title="Map Layers"
          style={{
            width: 28,
            height: 28,
            borderRadius: 4,
            border: "none",
            background: "#2E6DA4",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 5px rgba(0,0,0,0.4)",
          }}
        >
          <Layers size={14} color="#fff" />
        </button>
        {layersMenuOpen && (
          <div
            style={{
              position: "absolute",
              top: "34px",
              right: 0,
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
              minWidth: "140px",
              overflow: "hidden",
              border: "1px solid #EEF2F6",
            }}
          >
            {Object.keys(BASE_LAYERS).map((name) => (
              <div
                key={name}
                onClick={() => {
                  setActiveBaseLayer(name);
                  setLayersMenuOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  fontSize: "12.5px",
                  cursor: "pointer",
                  fontWeight: activeBaseLayer === name ? 700 : 500,
                  color: activeBaseLayer === name ? "#2E6DA4" : "#334155",
                  background: activeBaseLayer === name ? "#EFF6FA" : "transparent",
                }}
              >
                {name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const cardStyle = {
  background: "#fff",
  border: "1px solid #EAEEF3",
  borderRadius: "12px",
  padding: "12px 16px",
  boxShadow: "0 1px 2px rgba(15,45,66,0.03), 0 2px 8px rgba(15,45,66,0.04)",
};

const sectionLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "11px",
  fontWeight: 700,
  color: "#5B7A93",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: "14px",
  border: "1.5px solid #DCE4EC",
  borderRadius: "10px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
  color: "#0F2D42",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
};

// Auto-derived values (Severity, Event Status, Sensor Network, Communication
// Type) are shown in the same field grid as editable inputs, but styled as
// read-only — they're computed from the ported classification logic, not
// meant to be hand-picked.
const readOnlyFieldStyle = {
  ...inputStyle,
  background: "#F8FAFC",
  fontWeight: 700,
  cursor: "default",
};

const fieldLabelStyle = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#5B7A93",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: "6px",
  display: "block",
};

const sectionTitleStyle = {
  fontSize: "13.5px",
  fontWeight: 800,
  color: "#0F2D42",
  margin: "0 0 2px 2px",
};

const CreateAlertForm = () => {
  const navigate = useNavigate();

  // Where this alert's telemetry is coming from, and the source-specific
  // metadata "Source Details" collects for whichever one is selected.
  const [alertSource, setAlertSource] = useState("SENSOR");
  const [sensorType, setSensorType] = useState(HAZARD_FIXED_SOURCE.FLOOD.sensorType);
  const [sensorNameId, setSensorNameId] = useState(HAZARD_FIXED_SOURCE.FLOOD.sensorNameId);
  // Where the sensor is physically located. Defaults to the initial Sensor
  // Name/ID's own location segment (see extractLocationFromSensorName) and
  // stays disabled/auto-derived from it (see the Source Details JSX below).
  const [sourceLocation, setSourceLocation] = useState(() => extractLocationFromSensorName(sensorNameId));
  // Measured Value's unit — scoped per hazard the same way Sensor Type is.
  // Feeds the payload's "unit" field and the Measured Value chip only; the
  // Warning/Alert/Level thresholds below and the auto-generated Alert
  // Description remain defined in each hazard's own canonical unit (see
  // HAZARDS above) regardless of which unit is picked here — a known,
  // deliberately unaddressed gap for this pass.
  const [measurementUnit, setMeasurementUnit] = useState(HAZARD_SENSOR_CONFIG.FLOOD.units[0]);

  const [hazardKey, setHazardKey] = useState("FLOOD");
  const hazard = HAZARDS[hazardKey];

  // Starts empty; auto-filled from Source Details' Sensor Name/ID (see the
  // location-auto-fill effect below) rather than picked manually.
  const [location, setLocation] = useState(null);
  // Full street/locality address for the picked lat/lng, resolved via
  // reverse geocoding — purely a display enrichment for the Location &
  // Sensor field; location.name (the region key) keeps driving submission
  // and the auto-generated description, unchanged.
  const [locationAddress, setLocationAddress] = useState(null);
  const [loadingLocationAddress, setLoadingLocationAddress] = useState(false);
  // Auto-generated per hazard type (see buildAlertTitle) once a location is
  // known, same "stays editable until hand-edited" treatment as Alert
  // Description below.
  const [alertTitle, setAlertTitle] = useState("");
  const [titleEdited, setTitleEdited] = useState(false);
  const [measuredValue] = useState(FIXED_MEASURED_VALUE);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [infrastructureImpact] = useState([]);
  const [descriptionText, setDescriptionText] = useState("");
  const [descriptionEdited, setDescriptionEdited] = useState(false);
  const [attachments] = useState([]);

  // Alert timing window — fixed at mount to "now" for Expected to Begin /
  // Alert Effective From, and 24 hours out for Alert Valid Until; disabled
  // in the JSX below rather than left editable (same fixed-demo-values
  // treatment as Source Details and Measured Value).
  const [expectedToBegin] = useState(() => toLocalDateTimeInputValue(new Date()));
  const [alertEffectiveFrom] = useState(() => toLocalDateTimeInputValue(new Date()));
  const [alertValidUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toLocalDateTimeInputValue(d);
  });

  // Resolve the full address for whichever lat/lng Source Details' Sensor
  // Name/ID resolved to, for display only. Stitches the geocoded village/
  // locality (when Google has one) onto the district/block already known —
  // Google's own administrative levels are unreliable out here (often just
  // a bare plus-code), but the district/block from location.name is always
  // known.
  useEffect(() => {
    if (!location) {
      setLocationAddress(null);
      return;
    }
    // One of the 6 curated blocks (see BLOCK_COORDINATES) — use its known
    // address as-is instead of Google's reverse geocode, whether it got
    // here via the Source Details auto-fill or a manual map pick of the
    // same block.
    const curated = location.name ? BLOCK_ADDRESS_BY_NAME[location.name.toUpperCase()] : null;
    if (curated) {
      setLocationAddress(curated);
      setLoadingLocationAddress(false);
      return;
    }
    let cancelled = false;
    setLoadingLocationAddress(true);
    const knownNames = location.name?.includes(",") ? location.name.split(",").map((s) => s.trim()) : [];
    reverseGeocode(location.lat, location.lng, knownNames).then((place) => {
      if (cancelled) return;
      const districtBlock = location.name?.includes(",") ? toDisplayRegionName(location.name) : null;
      const full =
        place && districtBlock
          ? districtBlock.toLowerCase().includes(place.toLowerCase())
            ? districtBlock
            : `${place}, ${districtBlock}`
          : place || districtBlock;
      setLocationAddress(full);
      setLoadingLocationAddress(false);
    });
    return () => {
      cancelled = true;
    };
  },
 // eslint-disable-next-line react-hooks/exhaustive-deps 
  [location?.lat, location?.lng, location?.name]);

  // Auto-fills "5. Impact & Location" (Location & Sensor, State/District/
  // Block, Coordinates) from Source Details' Location, so the operator
  // isn't forced to re-pick the same block on the map picker after already
  // choosing a Sensor Name/ID that names it. The map/current-location
  // pickers can still override this afterward — this only re-fires when
  // sourceLocation itself changes (i.e. a new Sensor Type/Name-ID/hazard),
  // not on every render.
  useEffect(() => {
    const preset = BLOCK_COORDINATES[sourceLocation];
    if (!preset) return;
    setLocation({
      name: `${preset.district}, ${preset.block}`,
      lat: preset.lat,
      lng: preset.lng,
      sensorId: sensorNameId,
      regionId: preset.regionId,
      regionDetails: { state: "Ladakh", district: preset.district, block: preset.block },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceLocation]);

  const selectHazard = (key) => {
    setHazardKey(key);
    setLocation(null);
    setLastResult(null);
    setTitleEdited(false);
    setDescriptionEdited(false);
    // Sensor Type / Sensor Name-ID / Unit are all scoped to the hazard and
    // pinned to one fixed demo sensor each (see HAZARD_FIXED_SOURCE) — reset
    // every one of them (and the Location that Sensor Name-ID auto-fills)
    // to that hazard's fixed value rather than leaving a stale mismatch.
    const config = HAZARD_SENSOR_CONFIG[key] || HAZARD_SENSOR_CONFIG.FLOOD;
    const fixed = HAZARD_FIXED_SOURCE[key] || HAZARD_FIXED_SOURCE.FLOOD;
    setSensorType(fixed.sensorType);
    setSensorNameId(fixed.sensorNameId);
    setSourceLocation(extractLocationFromSensorName(fixed.sensorNameId));
    setMeasurementUnit(config.units[0]);
  };

  const numericValue = Number(measuredValue);
  const isValidValue = true;
  const isBelowWarningMin = false;
  const isUsableValue = true;

  // Severity is pinned to each hazard's own "Level 3 – Major" band rather
  // than derived from the (also fixed) Measured Value — this is a demo tool,
  // not a live sensor feed, so every stimulated alert always reads as a
  // Major-severity ALERT regardless of hazard type.
  const rawStatus = "ALERT";
  const sendEventStatus = "ALERT";
  const matchedLevel = hazard.levels.find((lvl) => lvl.level === 3) || hazard.levels[hazard.levels.length - 1];
  const severity = matchedLevel.label;
  const statusColor = LEVEL_COLORS[matchedLevel.level - 1];

  const autoDescription = isUsableValue && location
    ? buildAlertDescription(hazardKey, sendEventStatus, numericValue, location.name, hazard.critical)
    : "";
  const autoTitle = location ? buildAlertTitle(hazardKey, location.regionDetails) : "";

  // Same "stays editable until hand-edited" treatment as Alert Description
  // below — auto-fills once a location is known, but a manual edit pauses
  // further auto-regeneration. Switching hazard type resets it (see
  // selectHazard).
  useEffect(() => {
    if (!titleEdited) setAlertTitle(autoTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTitle, titleEdited]);

  // The description is auto-generated from the fields above, but stays
  // editable — once the operator hand-edits it, further auto-regeneration
  // is paused so their wording isn't silently overwritten by a later
  // measured-value/location tweak. Switching hazard type resets it (see
  // selectHazard) since an old description wouldn't make sense anymore.
  useEffect(() => {
    if (!descriptionEdited) setDescriptionText(autoDescription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDescription, descriptionEdited]);

  // Sensor is the only source this form actually creates alerts from (see
  // the ALERT_SOURCES comment above), so this is always the Sensor shape.
  const buildSourceDetails = () => ({
    sensorType,
    sensorNameId,
    location: sourceLocation.trim(),
  });

  // The create-manual endpoint takes multipart/form-data, not JSON — the
  // alert fields go in as a single JSON-stringified "alert" part alongside
  // "infrastructureImpact" (comma-joined) and one or more "files" parts.
  const buildAlertJson = () => ({
    hazardType: hazardDisplayName(hazardKey),
    eventStatus: sendEventStatus,
    severity: toApiSeverity(severity),
    latitude: Number(location.lat.toFixed(6)),
    longitude: Number(location.lng.toFixed(6)),
    // Sends the same full address shown in the Location & Sensor field
    // (e.g. "Sankoo, Kargil, Kargil") rather than the bare district/block
    // key, per explicit request.
    regionName: locationAddress || toDisplayRegionName(location.name),
    // Structured breakdown of the same location, shown in "5. Impact &
    // Location"'s District/Block fields — sent alongside regionName rather
    // than making the backend re-parse it.
    state: location.regionDetails?.state || null,
    district: location.regionDetails?.district || null,
    block: location.regionDetails?.block || null,
    // GIS region id for the block Source Details' Location resolved to (see
    // BLOCK_COORDINATES) — null if that lookup ever misses.
    regionId: location.regionId || null,
    sensorNetwork: sensorNameId,
    alertTitle: alertTitle.trim(),
    measuredValue: numericValue,
    thresholdValue: hazard.critical,
    unit: measurementUnit,
    communicationType: COMMUNICATION_TYPE,
    alertDescription: descriptionText,
    sourceType: alertSource,
    sourceDetails: buildSourceDetails(),
    onset: expectedToBegin ? new Date(expectedToBegin).toISOString() : null,
    effective: new Date(alertEffectiveFrom).toISOString(),
    expires: new Date(alertValidUntil).toISOString(),
  });

  const handleSubmit = async () => {
    if (!alertTitle.trim()) {
      toast.error("Enter an alert title before creating the alert.");
      return;
    }
    if (!location) {
      toast.error("Select a location (use the map picker or your current location) before creating the alert.");
      return;
    }
    if (!isValidValue) {
      toast.error("Enter a valid measured value before creating the alert.");
      return;
    }
    if (isBelowWarningMin) {
      toast.error(`Measured value can't be below the warning threshold (${hazard.warning[0]} ${hazard.unit}).`);
      return;
    }
    if (!alertEffectiveFrom || !alertValidUntil) {
      toast.error("Set the Alert Effective From and Alert Valid Until times before creating the alert.");
      return;
    }
    if (new Date(alertValidUntil) <= new Date(alertEffectiveFrom)) {
      toast.error("Alert Valid Until must be after Alert Effective From.");
      return;
    }
    setSubmitting(true);
    const alertJson = buildAlertJson();

    try {
      const formData = new FormData();
      formData.append("alert", JSON.stringify(alertJson));
      formData.append("infrastructureImpact", infrastructureImpact.join(","));
      attachments.forEach((file) => formData.append("files", file));

      const res = await postDataWithBodyEvent(EndPoints.createDisasterAlert, formData);
      const alertId = res?.alertId || res?.id;
      if (res && (alertId || res.status === "success")) {
        setLastResult({ alertId: alertId || "—", payload: alertJson });
        toast.success(res?.message || "Alert stimulated successfully.");
        // Pass a fresh timestamp (not just a boolean) so the target screen's
        // refresh effect fires even if it happens to already be mounted —
        // a plain `true` wouldn't change on a second alert creation in a row.
        navigate("/Disaster-acknowledgereport", { state: { refreshAt: Date.now() } });
      } else {
        toast.error("Failed to stimulate alert. The alerts service may be unreachable.");
      }
    } catch (err) {
      console.error("Stimulate alert failed:", err);
      toast.error("Failed to stimulate alert. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {/* ── Hero banner ── */}
      <div
        style={{
          background: "linear-gradient(120deg, #EAF4FB 0%, #F5FAFD 100%)",
          border: "1px solid #DCEEFA",
          borderRadius: "12px",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <div
          style={{
            width: "34px",
            height: "34px",
            borderRadius: "9px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 1px 3px rgba(15,45,66,0.08)",
          }}
        >
          <AlertTriangle size={17} color="#2E6DA4" />
        </div>
        <div>
          <div style={{ fontSize: "14.5px", fontWeight: 800, color: "#0F2D42", letterSpacing: "-0.01em" }}>
            Stimulate Alert
          </div>
          <div style={{ fontSize: "11.5px", color: "#5B7A93", marginTop: "2px" }}>
            Log a new hazard alert from sensor telemetry or a manual field observation.
          </div>
        </div>
      </div>

      {/* ── 1. Select Alert Source ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={sectionTitleStyle}>1. Select Alert Source</div>
        <div style={cardStyle}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {ALERT_SOURCES.map((s) => {
              const Icon = s.icon;
              const active = s.key === alertSource;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => {
                    // Third-Party API alerts (NDMA/IMD/DGRE/SDMA) are ingested
                    // and reviewed on the CAP Alerts page, not created here —
                    // picking this card hands the operator off there instead
                    // of just switching Source Details' fields.
                    if (s.key === "THIRD_PARTY_API") {
                      navigate("/Disaster-acknowledgereport/cap-alerts");
                      return;
                    }
                    setAlertSource(s.key);
                  }}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    textAlign: "left",
                    gap: "10px",
                    width: "260px",
                    padding: "8px 12px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: active ? "1.5px solid #2E6DA4" : "1.5px solid #E9EEF3",
                    background: active ? "#2E6DA412" : "#FAFBFC",
                    transition: "all 0.16s ease",
                  }}
                >
                  {active && (
                    <span
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: "#2E6DA4",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircle2 size={8} color="#fff" strokeWidth={3} />
                    </span>
                  )}
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      background: active ? "#2E6DA4" : "#EEF2F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={14} color={active ? "#fff" : "#64748B"} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "12px", fontWeight: 700, color: active ? "#2E6DA4" : "#334155" }}>{s.label}</div>
                    <div style={{ fontSize: "10.5px", color: "#94A3B8", lineHeight: 1.3 }}>{s.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── 2. Hazard Information ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={sectionTitleStyle}>2. Hazard Information</div>
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "16px", marginBottom: "8px" }}>
            <div style={{ ...sectionLabelStyle, marginBottom: 0 }}>
              Disaster <span style={{ color: "#DC2626" }}>*</span>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: "8px" }}>
            {Object.values(HAZARDS).map((h) => {
              const Icon = h.icon;
              const active = h.key === hazardKey;
              return (
                <button
                  key={h.key}
                  type="button"
                  onClick={() => selectHazard(h.key)}
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "5px",
                    padding: "9px 6px 8px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: active ? `1.5px solid ${h.color}` : "1.5px solid #E9EEF3",
                    background: active ? `${h.color}12` : "#FAFBFC",
                    boxShadow: active ? `0 4px 14px ${h.color}2A` : "none",
                    transform: active ? "translateY(-1px)" : "none",
                    transition: "all 0.16s ease",
                  }}
                >
                  {active && (
                    <span
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: h.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircle2 size={10} color="#fff" strokeWidth={3} />
                    </span>
                  )}
                  <div
                    style={{
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      background: active ? h.color : "#EEF2F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={15} color={active ? "#fff" : "#64748B"} />
                  </div>
                  <span style={{ fontSize: "11.5px", fontWeight: 700, color: active ? h.color : "#334155" }}>{h.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── 3. Source Details ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={sectionTitleStyle}>
          3. Source Details ({ALERT_SOURCES.find((s) => s.key === alertSource)?.label})
        </div>
        <div style={cardStyle}>
          {alertSource === "SENSOR" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" }}>
              <div>
                <label style={fieldLabelStyle}>
                  Sensor Type <span style={{ color: "#DC2626" }}>*</span>
                </label>
                {/* Pinned to one fixed demo sensor per hazard (see
                    HAZARD_FIXED_SOURCE) rather than a picker — disabled so
                    it can't drift from the value that also drives Location. */}
                <input
                  type="text"
                  value={sensorType}
                  disabled
                  style={{ ...inputStyle, ...readOnlyFieldStyle, cursor: "not-allowed" }}
                />
              </div>
              <div>
                <label style={fieldLabelStyle}>
                  Sensor Name / ID <span style={{ color: "#DC2626" }}>*</span>
                </label>
                <input
                  type="text"
                  value={sensorNameId}
                  disabled
                  style={{ ...inputStyle, ...readOnlyFieldStyle, cursor: "not-allowed" }}
                />
              </div>
              <div>
                <label style={fieldLabelStyle}>
                  Location <span style={{ color: "#DC2626" }}>*</span>
                </label>
                {/* Always auto-derived from the Sensor Name/ID (see
                    extractLocationFromSensorName) — disabled rather than
                    freely editable, so it can't drift from what was
                    actually picked above. */}
                <input
                  type="text"
                  value={sourceLocation}
                  disabled
                  placeholder="e.g. Leh, Ladakh"
                  style={{ ...inputStyle, ...readOnlyFieldStyle, cursor: "not-allowed" }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── 4. Alert Details ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={sectionTitleStyle}>4. Alert Details</div>
        <div style={cardStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1fr)", gap: "16px", alignItems: "start" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <label style={{ ...fieldLabelStyle, marginBottom: 0 }}>
                  Alert Title <span style={{ color: "#DC2626" }}>*</span>
                </label>
                {titleEdited && (
                  <button
                    type="button"
                    onClick={() => {
                      setTitleEdited(false);
                      setAlertTitle(autoTitle);
                    }}
                    style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "11px", fontWeight: 700, color: "#2E6DA4", padding: 0 }}
                  >
                    Reset to auto-generated
                  </button>
                )}
              </div>
              <input
                type="text"
                value={alertTitle}
                onChange={(e) => {
                  setAlertTitle(e.target.value);
                  setTitleEdited(true);
                }}
                maxLength={150}
                placeholder="e.g. Flood Warning at Leh, Diskit"
                style={inputStyle}
              />
            </div>

            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ ...sectionLabelStyle, marginBottom: "8px", flex: 1 }}>
                  <Activity size={13} /> Measured Value <span style={{ color: "#DC2626" }}>*</span>
                </div>
                {/* Invisible clone of the unit select below, purely to
                    reserve that column's width so the Severity label lines
                    up with the Severity badge underneath it. */}
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 700,
                    padding: "0 12px",
                    whiteSpace: "nowrap",
                    visibility: "hidden",
                  }}
                >
                  {measurementUnit}
                </span>
                <label style={{ ...fieldLabelStyle, marginBottom: "8px", flex: "0 0 150px", textAlign: "left" }}>Severity</label>
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                {/* Pinned to a fixed demo reading (see FIXED_MEASURED_VALUE)
                    — disabled rather than editable, same treatment as
                    Sensor Type/Name-ID above. */}
                <input
                  type="text"
                  inputMode="decimal"
                  value={measuredValue}
                  disabled
                  style={{
                    ...inputStyle,
                    ...readOnlyFieldStyle,
                    flex: 1,
                    fontSize: "16px",
                    fontWeight: 700,
                    cursor: "not-allowed",
                  }}
                />
                {/* Pinned to the hazard's own canonical unit (see HAZARDS)
                    — disabled rather than a picker, same fixed-demo-values
                    treatment as Sensor Type/Name-ID and Measured Value. */}
                <div
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 700,
                    color: "#5B7A93",
                    padding: "9px 8px",
                    background: "#F1F5F9",
                    borderRadius: "10px",
                    whiteSpace: "nowrap",
                    cursor: "not-allowed",
                  }}
                >
                  {measurementUnit}
                </div>
                <div
                  style={{
                    ...readOnlyFieldStyle,
                    width: "auto",
                    flex: "0 0 150px",
                    padding: "9px 8px",
                    fontSize: "11px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: rawStatus ? statusColor : "#94A3B8",
                  }}
                >
                  {rawStatus ? (severity === "LOW" ? "Low" : severity) : "—"}
                </div>
              </div>
            </div>
          </div>

          {isBelowWarningMin && (
            <div style={{ fontSize: "11.5px", fontWeight: 700, color: "#DC2626", marginTop: "8px" }}>
              Minimum allowed is {hazard.warning[0]} {hazard.unit} — value will be raised on save.
            </div>
          )}
          <div style={{ fontSize: "11.5px", color: "#94A3B8", marginTop: "10px", textAlign: "right" }}>
            Warning {hazard.warning[0]}–{hazard.warning[1]} ·{" "}
            {hazard.levels.map((lvl, i) => (
              <React.Fragment key={lvl.level}>
                {i > 0 && " · "}
                {lvl.label.split("– ")[1]} {lvl.min}–{lvl.max}
              </React.Fragment>
            ))}{" "}
            {hazard.unit}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", marginTop: "12px" }}>
            {/* All three are pinned at mount (see the useState initializers
                above: "now", "now", "now + 24h") and disabled rather than
                editable — same fixed-demo-values treatment as Source
                Details and Measured Value above. */}
            <div>
              <label style={fieldLabelStyle}>Expected to Begin</label>
              <input
                type="datetime-local"
                value={expectedToBegin}
                disabled
                style={{ ...inputStyle, ...readOnlyFieldStyle, cursor: "not-allowed" }}
              />
            </div>
            <div>
              <label style={fieldLabelStyle}>
                Alert Effective From <span style={{ color: "#DC2626" }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={alertEffectiveFrom}
                disabled
                style={{ ...inputStyle, ...readOnlyFieldStyle, cursor: "not-allowed" }}
              />
            </div>
            <div>
              <label style={fieldLabelStyle}>
                Alert Valid Until <span style={{ color: "#DC2626" }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={alertValidUntil}
                disabled
                style={{ ...inputStyle, ...readOnlyFieldStyle, cursor: "not-allowed" }}
              />
            </div>
          </div>

          {/* Alert Description — auto-generated from the hazard + measured
              value + location, but stays editable (see the useEffect wiring
              descriptionText below). */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px", marginTop: "12px" }}>
            <div style={{ ...sectionLabelStyle, marginBottom: 0 }}>
              Alert Description <span style={{ color: "#DC2626" }}>*</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {descriptionEdited && (
                <button
                  type="button"
                  onClick={() => {
                    setDescriptionEdited(false);
                    setDescriptionText(autoDescription);
                  }}
                  style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "11px", fontWeight: 700, color: "#2E6DA4", padding: 0 }}
                >
                  Reset to auto-generated
                </button>
              )}
              <span style={{ fontSize: "11px", color: "#94A3B8" }}>{descriptionText.length} / 1000</span>
            </div>
          </div>
          <textarea
            value={descriptionText}
            onChange={(e) => {
              setDescriptionText(e.target.value);
              setDescriptionEdited(true);
            }}
            maxLength={1000}
            rows={3}
            style={{
              ...inputStyle,
              resize: "vertical",
              fontSize: "12.5px",
              lineHeight: 1.6,
              fontFamily: "'Cascadia Code', Consolas, monospace",
              background: "#F8FAFC",
              minHeight: "40px",
            }}
          />
        </div>
      </div>

      {/* ── 5. Impact & Location ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={sectionTitleStyle}>5. Impact &amp; Location</div>

        <div style={cardStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: "18px", alignItems: "start" }}>
            <div>
              <div style={sectionLabelStyle}>
                <MapPin size={13} /> Location &amp; Sensor <span style={{ color: "#DC2626" }}>*</span>
              </div>
              {/* Read-only — Location is fully auto-derived from Source
                  Details' Sensor Name/ID (see the location-auto-fill effect
                  above), so this no longer opens the map picker on click. */}
              <div
                style={{
                  ...inputStyle,
                  ...readOnlyFieldStyle,
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600, color: location ? "#0F2D42" : "#94A3B8" }}>
                  <MapPin size={14} color={location ? "#2E6DA4" : "#94A3B8"} />
                  {location
                    ? loadingLocationAddress
                      ? "Resolving address…"
                      : locationAddress || toDisplayRegionName(location.name)
                    : "Select a location"}
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "14px" }}>
                {[
                  ["District", location?.regionDetails?.district],
                  ["Block", location?.regionDetails?.block],
                ].map(([label, value]) => (
                  <div key={label}>
                    <label style={fieldLabelStyle}>{label}</label>
                    <div style={{ padding: "8px 10px", background: "#F8FAFC", borderRadius: "10px", border: "1px solid #EEF2F6" }}>
                      <span style={{ fontSize: "12px", color: value ? "#334155" : "#94A3B8", fontWeight: 600 }}>
                        {value ? toTitleCase(value) : "—"}
                      </span>
                    </div>
                  </div>
                ))}
                <div>
                  <label style={fieldLabelStyle}>Coordinates</label>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", background: "#F8FAFC", borderRadius: "10px", border: "1px solid #EEF2F6" }}>
                    <MapPin size={14} color="#64748B" />
                    <span style={{ fontSize: "12px", color: location ? "#334155" : "#94A3B8" }}>
                      {location ? `${location.lat.toFixed(4)}°, ${location.lng.toFixed(4)}°` : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label style={fieldLabelStyle}>Location on Map</label>
              <LocationPreviewMap
                location={location}
                color={hazard.color}
                popupInfo={{
                  Icon: hazard.icon,
                  hazardLabel: hazard.label,
                  locationLabel: location ? (locationAddress || toDisplayRegionName(location.name)) : "",
                  severity,
                  sensorNameId,
                  measuredValue,
                  unit: measurementUnit,
                }}
              />
            </div>
          </div>
        </div>

      </div>

      {/* Submit — kept outside any card, as a plain page-level action
          rather than part of the Impact & Location card. */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !isUsableValue}
          style={{
            minWidth: "200px",
            padding: "11px 22px",
            fontSize: "13.5px",
            fontWeight: 700,
            color: "#fff",
            background:
              submitting || !isUsableValue
                ? "#B9C2CC"
                : "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)",
            border: "none",
            borderRadius: "10px",
            cursor: submitting || !isUsableValue ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            boxShadow: submitting || !isUsableValue ? "none" : "0 3px 10px rgba(46,109,164,0.22)",
            transition: "transform 0.12s ease, box-shadow 0.12s ease",
          }}
        >
          {submitting ? <Loader2 size={15} className="spin" /> : <Send size={15} />}
          {submitting ? "Creating…" : "Stimulate Alert"}
        </button>

        {lastResult && (
          <div
            style={{
              width: "100%",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              padding: "13px 14px",
              borderRadius: "12px",
              background: "#F0FDF4",
              border: "1px solid #BBF7D0",
            }}
          >
            <CheckCircle2 size={18} color="#166534" style={{ flexShrink: 0, marginTop: "1px" }} />
            <div style={{ fontSize: "12.5px", color: "#166534" }}>
              <div style={{ fontWeight: 700 }}>Created — Alert ID {lastResult.alertId}</div>
              <div style={{ color: "#3F6B4C", marginTop: "2px" }}>
                {lastResult.payload.hazardType} · {lastResult.payload.eventStatus} · {lastResult.payload.regionName}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
      `}</style>
    </div>
  );
};

const CreateAlert = () => {
  const layoutData = [
    {
      x: 0,
      y: 0,
      width: 12,
      height: 20,
      id: 0,
      component: (
        <div style={{ padding: "20px", overflowY: "auto", height: "100%", boxSizing: "border-box" }}>
          <CreateAlertForm />
        </div>
      ),
    },
  ];

  return (
    <div className="grid-stack grid-stack-instance-1">
      <GridItem layoutData={layoutData} />
    </div>
  );
};

export default CreateAlert;
